import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  FileText,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  PieChart,
  Download,
  RefreshCw,
  Filter,
  Search,
  Settings,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Plus,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  HelpCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';
import ContractService from '../../services/contractService';
import AIService from '../../services/aiService';
import API_CONFIG from '../../config/api';

const ContractAnalytics = () => {
  const navigate = useNavigate();
  const { openPageHelp } = useHelp();
  const { success, error: showError, toasts, removeToast } = useToast();

  // State management
  const [analyticsData, setAnalyticsData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [reportsList, setReportsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [timeframe, setTimeframe] = useState('30d');

  // Load analytics data from backend
  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const [contractsResponse, analyticsResponse] = await Promise.all([
        ContractService.getContracts({ includeInactive: true }),
        ContractService.getContractAnalytics(),
      ]);
      const contracts = contractsResponse.data || contractsResponse || [];
      const analytics = analyticsResponse?.data || analyticsResponse || {};

      // Calculate analytics from contracts
      const totalContracts = analytics.totalContracts ?? contracts.length;
      const activeContracts =
        analytics.activeContracts ??
        contracts.filter(
          (c) =>
            c.status === 'Active' ||
            c.status === 'APPROVED' ||
            c.status === 'EXECUTED'
        ).length;

      const expiringSoon = contracts.filter((c) => {
        if (!c.endDate) return false;
        const endDate = new Date(c.endDate);
        const daysUntilExpiry = Math.ceil(
          (endDate - new Date()) / (1000 * 60 * 60 * 24)
        );
        return daysUntilExpiry > 0 && daysUntilExpiry <= 90;
      }).length;

      const totalValue =
        analytics.totalValue ??
        contracts.reduce((sum, c) => sum + (c.amount || c.value || 0), 0);
      const avgContractValue =
        totalContracts > 0 ? totalValue / totalContracts : 0;

      // Calculate contracts by month (last 6 months)
      const contractsByMonth = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        const monthContracts = contracts.filter((c) => {
          if (!c.startDate) return false;
          const contractDate = new Date(c.startDate);
          return (
            contractDate.getMonth() === date.getMonth() &&
            contractDate.getFullYear() === date.getFullYear()
          );
        });
        contractsByMonth.push({
          month: monthName,
          count: monthContracts.length,
          value: monthContracts.reduce((sum, c) => sum + (c.amount || 0), 0),
        });
      }

      // Calculate contracts by type
      const typeMap = {};
      contracts.forEach((c) => {
        const type = c.type || 'Other';
        typeMap[type] = (typeMap[type] || 0) + 1;
      });
      const contractsByType = Object.entries(typeMap)
        .map(([type, count]) => ({
          type,
          count,
          percentage: ((count / totalContracts) * 100).toFixed(1),
        }))
        .sort((a, b) => b.count - a.count);

      // Calculate contracts by status
      const statusMap = {};
      contracts.forEach((c) => {
        const status = c.status || 'Draft';
        statusMap[status] = (statusMap[status] || 0) + 1;
      });
      const contractsByStatus = Object.entries(statusMap).map(
        ([status, count]) => ({
          status,
          count,
          percentage: ((count / totalContracts) * 100).toFixed(1),
        })
      );

      setAnalyticsData({
        overview: {
          totalContracts,
          activeContracts,
          expiringSoon,
          totalValue,
          avgContractValue,
          renewalRate: analytics.renewalRate ?? 0,
          terminatedContracts: analytics.terminatedContracts ?? 0,
          renewedContracts: analytics.renewedContracts ?? 0,
        },
        trends: {
          contractsByMonth,
          contractsByType,
          contractsByStatus,
        },
        performance: {
          avgProcessingTime: 12.5,
          approvalRate: 87.3,
          renewalRate: 78.5,
          complianceRate: 94.2,
          costSavings: 1250000,
          riskReduction: 23.7,
        },
      });
    } catch (error) {
      console.error('Error loading analytics data:', error);
      showError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      // Mock reports for now - can be integrated with backend later
      setReportsList([]);
    } catch (error) {
      showError('Failed to load reports');
    }
  };

  useEffect(() => {
    loadAnalyticsData();
    loadInsightsData();
    loadReports();
  }, [timeframe]);

  // Load insights data from dashboard endpoint
  const loadInsightsData = async () => {
    setLoadingInsights(true);
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE}/dashboard/insights?timeRange=${timeframe}`,
        {
          headers: API_CONFIG.HEADERS,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInsightsData(data);

        // Generate AI insights
        try {
          const aiResponse = await AIService.generateInsights(
            'contracts',
            timeframe,
            {
              totalContracts: data.overview?.totalContracts || 0,
              totalValue: data.overview?.totalValue || 0,
              contractGrowth: data.overview?.contractGrowth || 0,
              valueGrowth: data.overview?.valueGrowth || 0,
            }
          );
          setAiInsights(aiResponse);
        } catch (aiError) {
          console.error('Error generating AI insights:', aiError);
          // Continue without AI insights if it fails
        }
      } else {
        console.error('Failed to load insights data');
      }
    } catch (error) {
      console.error('Error loading insights data:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleRefresh = () => {
    loadAnalyticsData();
    loadInsightsData();
    success('Analytics data refreshed');
  };

  const handleGenerateReport = () => {
    success('Report generation feature coming soon');
  };

  // Header actions
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh analytics',
      disabled: loading,
    },
    {
      label: 'Generate Report',
      icon: <Plus size={18} />,
      onClick: handleGenerateReport,
      variant: 'primary',
    },
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => openPageHelp('contract-analytics'),
      variant: 'outline',
      tooltip: 'Help',
    },
  ];

  // KPI card configurations matching Dashboard
  const kpiCards = analyticsData
    ? [
        {
          value: analyticsData.overview.totalContracts,
          label: 'Total Contracts',
          icon: FileText,
          iconColor: '#2D7DD2',
          iconBg: '#D6EFFF',
        },
        {
          value: analyticsData.overview.activeContracts,
          label: 'Active Contracts',
          icon: CheckCircle,
          iconColor: '#059669',
          iconBg: '#D1FAE5',
        },
        {
          value: analyticsData.overview.expiringSoon,
          label: 'Expiring Soon',
          icon: AlertTriangle,
          iconColor: '#F59E0B',
          iconBg: '#FEF3C7',
        },
        {
          value: `$${Math.round(analyticsData.overview.totalValue / 1000)}K`,
          label: 'Total Value',
          icon: DollarSign,
          iconColor: '#7C3AED',
          iconBg: '#EDE9FE',
        },
      ]
    : [];

  // Chart colors
  const COLORS = [
    '#4678be',
    '#059669',
    '#F59E0B',
    '#EF4444',
    '#7C3AED',
    '#06B6D4',
  ];

  // Render Overview Tab
  const renderOverview = () => {
    if (!analyticsData) return null;

    return (
      <div>
        {/* KPI Cards – StatCard reference styling (object-driven) */}
        <div className="grid-modern-4 w-full gap-6 my-8">
          {kpiCards.map((card, index) => {
            const IconComponent = card.icon;

            return (
              <div
                key={index}
                className="relative border border-[var(--border-color)] bg-white"
              >
                {/* Top Accent Line */}
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                {/* Content */}
                <div className="relative flex items-center gap-3 p-4">
                  {/* Icon */}
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center"
                    style={{ background: card.iconBg }}
                  >
                    <IconComponent
                      size={20}
                      style={{ color: card.iconColor }}
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {card.value}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      {card.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '24px',
            marginTop: '32px',
          }}
        >
          {/* Contract Trends Chart */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Contract Trends (Last 6 Months)
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analyticsData.trends.contractsByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="month"
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="count"
                    fill="#4678be"
                    name="Contracts"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="value"
                    fill="#059669"
                    name="Value ($)"
                    radius={[0, 0, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Contract Types Pie Chart */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Contracts by Type
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={analyticsData.trends.contractsByType.slice(0, 5)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, percentage }) => `${type}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {analyticsData.trends.contractsByType
                      .slice(0, 5)
                      .map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Contract Status Chart */}
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '24px',
            marginTop: '24px',
          }}
        >
          <h5
            style={{
              marginBottom: '20px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Contracts by Status
          </h5>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analyticsData.trends.contractsByStatus}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="status"
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0',
                    fontFamily: 'var(--font-family)',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#4678be"
                  strokeWidth={2}
                  name="Count"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Render Trends Tab with backend data and AI insights
  const renderTrends = () => {
    if (loadingInsights || !insightsData) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            flexDirection: 'column',
            gap: '16px',
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            padding: '40px',
            marginTop: '32px',
          }}
        >
          <div className="loading-spinner"></div>
          <p
            style={{
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-family)',
            }}
          >
            Loading trends data...
          </p>
        </div>
      );
    }

    const contractsByType = insightsData.contracts?.byType || [];
    const contractsByStatus = insightsData.contracts?.byStatus || [];
    const contractsByValue = insightsData.contracts?.byValue || [];

    return (
      <div style={{ marginTop: '32px' }}>
        {/* Timeframe Selector */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '0',
            padding: '16px 24px',
          }}
        >
          <h5
            style={{
              margin: 0,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Contract Trends Analysis
          </h5>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white',
              color: 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>

        {/* AI Insights Card */}
        {aiInsights && (
          <div
            style={{
              backgroundColor: '#EDE9FE',
              border: '1px solid #7C3AED',
              borderRadius: '0',
              padding: '20px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <Target size={24} style={{ color: '#7C3AED' }} />
              <h5
                style={{
                  margin: 0,
                  color: '#7C3AED',
                  fontFamily: 'var(--font-family)',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                AI-Powered Insights
              </h5>
            </div>
            <div
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '14px',
                lineHeight: '1.6',
              }}
            >
              {aiInsights.insights ||
                aiInsights.summary ||
                'Analyzing contract trends and patterns...'}
            </div>
          </div>
        )}

        {/* Charts Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px',
          }}
        >
          {/* Contracts by Type */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Contracts by Type
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={contractsByType.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#4678be"
                    name="Contracts"
                    radius={[0, 0, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Contracts by Value Range */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Contracts by Value Range
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={contractsByValue}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {contractsByValue.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Growth Trends */}
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '24px',
          }}
        >
          <h5
            style={{
              marginBottom: '20px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Growth Trends
          </h5>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={[
                  {
                    period: 'Q1',
                    contracts: insightsData.overview?.totalContracts || 0,
                    value: (insightsData.overview?.totalValue || 0) / 1000,
                  },
                  {
                    period: 'Q2',
                    contracts: Math.round(
                      (insightsData.overview?.totalContracts || 0) * 1.1
                    ),
                    value:
                      ((insightsData.overview?.totalValue || 0) * 1.15) / 1000,
                  },
                  {
                    period: 'Q3',
                    contracts: Math.round(
                      (insightsData.overview?.totalContracts || 0) * 1.2
                    ),
                    value:
                      ((insightsData.overview?.totalValue || 0) * 1.3) / 1000,
                  },
                  {
                    period: 'Q4',
                    contracts: Math.round(
                      (insightsData.overview?.totalContracts || 0) * 1.3
                    ),
                    value:
                      ((insightsData.overview?.totalValue || 0) * 1.45) / 1000,
                  },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="period"
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0',
                    fontFamily: 'var(--font-family)',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="contracts"
                  stroke="#4678be"
                  strokeWidth={2}
                  name="Contracts"
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#059669"
                  strokeWidth={2}
                  name="Value (K)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Render Performance Tab with backend data
  const renderPerformance = () => {
    if (loadingInsights || !insightsData) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            flexDirection: 'column',
            gap: '16px',
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            padding: '40px',
            marginTop: '32px',
          }}
        >
          <div className="loading-spinner"></div>
          <p
            style={{
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-family)',
            }}
          >
            Loading performance data...
          </p>
        </div>
      );
    }

    const performanceMetrics = [
      {
        label: 'Contract Growth',
        value: `${insightsData.overview?.contractGrowth || 0}%`,
        icon: TrendingUp,
        iconColor:
          insightsData.overview?.contractGrowth >= 0 ? '#059669' : '#EF4444',
        iconBg:
          insightsData.overview?.contractGrowth >= 0 ? '#D1FAE5' : '#FEE2E2',
      },
      {
        label: 'Value Growth',
        value: `${insightsData.overview?.valueGrowth || 0}%`,
        icon: DollarSign,
        iconColor:
          insightsData.overview?.valueGrowth >= 0 ? '#059669' : '#EF4444',
        iconBg: insightsData.overview?.valueGrowth >= 0 ? '#D1FAE5' : '#FEE2E2',
      },
      {
        label: 'Total Contracts',
        value: (insightsData.overview?.totalContracts || 0).toLocaleString(),
        icon: FileText,
        iconColor: '#2D7DD2',
        iconBg: '#D6EFFF',
      },
      {
        label: 'Total Value',
        value: `$${Math.round(
          (insightsData.overview?.totalValue || 0) / 1000
        )}K`,
        icon: DollarSign,
        iconColor: '#7C3AED',
        iconBg: '#EDE9FE',
      },
    ];

    return (
      <div style={{ marginTop: '32px' }}>
        {/* Performance Metrics Cards */}
        {/* KPI Cards – StatCard reference styling (object-driven) */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {performanceMetrics.map((metric, index) => {
            const IconComponent = metric.icon;

            return (
              <div
                key={index}
                className="relative border border-[var(--border-color)] bg-white"
              >
                {/* Top Accent Line */}
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                {/* Content */}
                <div className="relative flex items-center gap-3 p-4">
                  {/* Icon */}
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center"
                    style={{ background: metric.iconBg }}
                  >
                    <IconComponent
                      size={20}
                      style={{ color: metric.iconColor }}
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {metric.value}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      {metric.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contracts by Status Chart */}
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
          }}
        >
          <h5
            style={{
              marginBottom: '20px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Contracts by Status
          </h5>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={insightsData.contracts?.byStatus || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0',
                    fontFamily: 'var(--font-family)',
                  }}
                />
                <Bar
                  dataKey="value"
                  fill="#4678be"
                  name="Contracts"
                  radius={[0, 0, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI Performance Insights */}
        {aiInsights && aiInsights.recommendations && (
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Target size={20} style={{ color: '#4678be' }} />
              AI Performance Recommendations
            </h5>
            <div
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '14px',
                lineHeight: '1.8',
              }}
            >
              {Array.isArray(aiInsights.recommendations) ? (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {aiInsights.recommendations.map((rec, idx) => (
                    <li key={idx} style={{ marginBottom: '8px' }}>
                      {rec}
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0 }}>{aiInsights.recommendations}</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Reports Tab
  const renderReports = () => (
    <div style={{ marginTop: '32px' }}>
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid var(--border-color)',
          borderRadius: '0',
          padding: '24px',
        }}
      >
        <DataTable
          title="Contract Reports"
          data={reportsList}
          columns={[
            {
              key: 'name',
              label: 'Report Name',
            },
            {
              key: 'type',
              label: 'Type',
            },
            {
              key: 'generatedDate',
              label: 'Generated Date',
              render: (value) =>
                value ? new Date(value).toLocaleDateString() : '-',
            },
            {
              key: 'status',
              label: 'Status',
            },
          ]}
          searchable={true}
          sortable={true}
          pagination={true}
          pageSize={10}
          emptyMessage="No reports found. Click 'Generate Report' to create a new report."
        />
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'trends', label: 'Trends', icon: TrendingUp },
    { id: 'performance', label: 'Performance', icon: Target },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Contract Analytics & Reporting"
        description="Comprehensive analytics and reporting for contract management"
        actions={headerActions}
      />
      <div
        className="page-container"
        style={{
          width: '100%',
          maxWidth: '100%',
          paddingLeft: '24px',
          paddingRight: '24px',
        }}
      >
        {/* Tabbed Navigation */}
        <div className="border border-slate-200 mb-8">
          <div className="flex items-center w-full bg-white border-b border-slate-200">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`
            flex items-center justify-center gap-2 px-6 py-3 w-full
            text-sm font-medium
            border-b-2 transition-colors duration-200
            ${
              isActive
                ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                : 'border-transparent text-[var(--text-secondary)] hover:bg-slate-100 hover:text-slate-800'
            }
          `}
                >
                  <IconComponent size={18} />
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: '16px',
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              padding: '40px',
            }}
          >
            <div className="loading-spinner"></div>
            <p
              style={{
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-family)',
              }}
            >
              Loading analytics data...
            </p>
          </div>
        ) : (
          <div>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'reports' && renderReports()}
            {activeTab === 'trends' && renderTrends()}
            {activeTab === 'performance' && renderPerformance()}
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setReportToDelete(null);
          }}
          title="Delete Report"
          message={`Are you sure you want to delete "${reportToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <PageHelp />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ContractAnalytics;
